import React from 'react';
import { defineMessages, FormattedMessage } from 'react-intl';
import { Link, useNavigate } from 'react-router-dom';
import { IUser } from 'src/entities/user';
import { getUserWithInvoice, postInvoice, postUserDetails } from 'src/services/api/user';
import FormValidator from 'src/services/formValidator';
import { userDetailsRules } from 'src/services/validations/userDetailsRules';
import Checkbox from '../form-components/Checkbox/Checkbox';
import PrimaryButton from '../form-components/PrimaryButton/PrimaryButton';
import ValidationMessage from '../form-components/ValidationMessage/ValidationMessage';
import PersonalDetails from './PersonalDetails/PersonalDetails';
import { Routes } from 'src/routes';
import globalMessages from 'src/services/globalMessages';
import { Section } from '../Section';
import Popup from '../Popup/Popup';
import { useForm, useSubscription } from 'src/hooks';
import { useReducer, useEffect, useState, useContext } from 'react';
import SuccessMessage from '../SuccessMessage/SuccessMessage';
import { removeToken } from 'src/services/api/authorization';
import UserInvoiceDetails from './InvoiceDetails/UserInvoiceDetails';
import { invoiceDetailsRules } from 'src/services/validations/invoiceDetailsRules';
import { AppContext } from 'src/contexts/AppContext';

const messages = defineMessages({
    userDetailsTitle: {
        id: 'userDetailsTitle',
        defaultMessage: 'Felhasználói fiók: <span>{userName}</span>'
    },
    isEmailNeed: {
        id: 'isEmailNeeded',
        defaultMessage: 'Kérek e-mailt a foglalásokról.'
    },
    notification: {
        id: 'notification',
        defaultMessage: 'Értesítés beállítása'
    },
    userDataChangeSuccess: {
        id: 'userDataChangeSuccess',
        defaultMessage: 'Sikeresen megváltoztattad az adataidat!'
    },
    userDataChangeSuccessWithEmailChange: {
        id: 'userDataChangeSuccessWithEmailChange',
        defaultMessage: 'Sikeresen megváltoztattad az adataidat! Mivel változott az email címed, kérlek jelentkezz be újra!'
    },
    personalSave: {
        id: 'personalSave',
        defaultMessage: 'Adatok mentése'
    },
    invoiceSave: {
        id: 'invoiceSave',
        defaultMessage: 'Számlázási adatok mentése'
    },
    invoiceInfo1: {
        id: 'invoiceInfo1',
        defaultMessage: 'Ha kitöltöd az alábbi mezőket, elmentjük a számlázási adataidat a fiókodhoz.'
    },
    invoiceInfo2: {
        id: 'invoiceInfo2',
        defaultMessage: 'A foglalás véglegesítésnél megadott számlázási adatok nem kerülnek mentésre.'
    }
});

type IUserAction =
    {
        type: 'userEdit',
        payload: { value: string | boolean, name: string }
    }
    | {
        type: 'invoiceEdit',
        payload: { value: string | boolean, name: string }
    }
    | {
        type: 'init',
        payload: IUser
    }

const reducer = (prevUser: IUser, action: IUserAction) => {
    switch (action.type) {
        case 'userEdit':
            return {
                ...prevUser,
                [action.payload.name]: action.payload.value
            }
        case 'invoiceEdit':
            return {
                ...prevUser, InvoiceDetails: {
                    ...prevUser.InvoiceDetails,
                    [action.payload.name]: action.payload.value,
                    ...((action.payload.name === 'PrivatePerson' && action.payload.value) &&
                        { TaxNumber: '' })
                }
            }
        default:
            return action.payload;
    }
}

export const initialUserState = {
    Id: -1,
    Name: '',
    Email: '',
    Phone: '',
    NewResEmailNeed: false,
    PrivacyStatementAccepted: false,
    TermsAccepted: false,
    InvoiceDetails: {
        InvoiceName: '',
        Country: '',
        City: '',
        StreetAddress: '',
        PostalCode: '',
        PrivatePerson: false,
        TaxNumber: ''
    }
}

const personalValidator = new FormValidator(userDetailsRules(true, true, true));
const invoiceValidator = new FormValidator(invoiceDetailsRules());

const UserDetails: React.FC = () => {
    const { actions: { setUser }, user: appUser, company } = useContext(AppContext);
    const [userForm, dispatch] = useReducer(reducer, appUser || initialUserState);
    const [originalName, setOriginalName] = useState(appUser?.Name);
    const [user, isLoading] = useSubscription(getUserWithInvoice);
    const [emailChanged, setEmailChanged] = useState(false);
    const navigate = useNavigate();

    useEffect(() => {
        if (user) {
            if (!user.InvoiceDetails) {
                user.InvoiceDetails = initialUserState.InvoiceDetails;
            }
            setOriginalName(user.Name);
            dispatch({ type: 'init', payload: user })
        }
    }, [user])

    const handleUserChange = (value: string | boolean, name: string) => dispatch({ type: 'userEdit', payload: { value, name } });
    const handleInvoiceChange = (value: string | boolean, name: string) => dispatch({ type: 'invoiceEdit', payload: { value, name } });

    const personalForm = useForm(personalValidator, { ...userForm }, () => postUserDetails({ ...userForm, InvoiceDetails: null }));
    const invoiceForm = useForm(invoiceValidator, { ...userForm.InvoiceDetails }, () => postInvoice(userForm.InvoiceDetails));

    useEffect(() => {
        if (personalForm.result || invoiceForm.result) {
            togglePopup();
            if (user?.Email !== userForm.Email) {
                setEmailChanged(true);
                setUser(null);
                removeToken();
            } else {
                setUser(userForm);
                setOriginalName(userForm.Name);
            }
        }
    }, [personalForm.result, invoiceForm.result])

    const togglePopup = () => {
        if (emailChanged && isPopupShown) {
            navigate(Routes.Login + '?customerReturnUrl=' + Routes.UserDetails);
        }
        setIsPopupShown();
    }

    const [isPopupShown, setIsPopupShown] = useReducer(b => !b, false);

    return (
        <section className="user-details">
            <h1 className="user-details__title"><FormattedMessage values={{ userName: originalName, span: (msg: any) => <span className="user-details__name">{msg}</span> }} {...messages.userDetailsTitle} /></h1>
            <div className="user-details__buttons">
                <Link className="button button--primary user-details__link user-details__reservations" to={Routes.UserReservations} >
                    <FormattedMessage {...globalMessages.userReservations} />
                </Link>
                <Link className="button button--primary user-details__link" to={Routes.HomeSlash} >
                    <FormattedMessage {...globalMessages.newReservation} />
                </Link>
            </div>

            <Section title={globalMessages.userData}>
                <PersonalDetails
                    autoFocus={true}
                    defaultUser={userForm}
                    validation={personalForm.validation}
                    handleUserChange={handleUserChange}
                    isEmailRequired={true}
                    isPhoneNumberRequired={true}
                    isNameRequired={true} />
                <Link className="personal-details-form__field user-form__secondary-button button button--secondary" to={Routes.ChangePassword} >
                    <FormattedMessage {...globalMessages.changePassword} />
                </Link>
            </Section>

            <div className="validation-message-container">
                <ValidationMessage message={personalForm.errorMessage} />
            </div>

            <Section title={messages.notification}>
                <Checkbox
                    className="personal-details-form__field"
                    fieldName="NewResEmailNeed"
                    checked={userForm.NewResEmailNeed}
                    onChange={handleUserChange} >
                    <FormattedMessage {...messages.isEmailNeed} />
                </Checkbox>
            </Section>

            <PrimaryButton className="user-form__submit" caption={messages.personalSave} onClick={personalForm.handleSubmit} />

            {(company?.PaymentSettings.IsBillingEnabled && !isLoading) &&
                <>
                    <Section title={globalMessages.invoiceDetails}>
                        <div className="invoice-details-info">
                            <p><FormattedMessage {...messages.invoiceInfo1} /></p>
                            <p><FormattedMessage {...messages.invoiceInfo2} /></p>
                        </div>
                        <UserInvoiceDetails
                            defaultUserInvoice={userForm.InvoiceDetails}
                            handleInvoiceChange={handleInvoiceChange}
                            validation={invoiceForm.validation} />
                    </Section>

                    <div className="validation-message-container">
                        <ValidationMessage message={invoiceForm.errorMessage} />
                    </div>

                    <PrimaryButton className="user-form__submit" caption={messages.invoiceSave} onClick={invoiceForm.handleSubmit} />
                </>
            }

            <Popup closePopup={togglePopup} visible={isPopupShown}>
                <SuccessMessage message={emailChanged ? messages.userDataChangeSuccessWithEmailChange : messages.userDataChangeSuccess} />
            </Popup>
        </section>
    )
}

export default UserDetails;
